import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from './Header';
import {
  Home,
  DollarSign,
  Calendar,
  Upload,
  FileUp,
  PlusCircle,
  CheckCircle,
  AlertCircle,
  Building,
  Users,
  CreditCard,
  Bed,
  Clock,
  X,
  FileSpreadsheet,
  Download,
  Info,
  FileText
} from 'lucide-react';

const ModalUploadHousesForm = () => {
  const server_app_api_php = window.SERVER_APP_API_PHP;
  const [businessLocations, setBusinessLocations] = useState([]);
  const [companyID] = useState(1);
  const [uploadMethod, setUploadMethod] = useState('');
  const [houseDetails, setHouseDetails] = useState({
    houseNumber: '',
    rentalsPaymentInterval: '',
    rentalPrice: '',
    rentalNumberOfRooms: '',
    rentalStatus: '',
    businessLocationID: '',
  });
  const [excelFile, setExcelFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [activeSection, setActiveSection] = useState('method');

  useEffect(() => {
    fetchBusinessLocations();
  }, []);

  const fetchBusinessLocations = async () => {
    const formData = new FormData();
    formData.append("apiType", 'getBusinessLocationHousesOnUploadForm');
    formData.append("companyID", companyID);
    
    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
      setBusinessLocations(response.data);
    } catch (error) {
      console.error("Error fetching business locations:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setHouseDetails(prev => ({ ...prev, [name]: value }));
  };

  const handleExcelFileChange = (e) => {
    setExcelFile(e.target.files[0]);
  };

  const handleSingleFormSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmitStatus({ type: '', message: '' });

    const formData = new FormData();
    formData.append('houseNumber', houseDetails.houseNumber);
    formData.append('rentalsPaymentInterval', houseDetails.rentalsPaymentInterval);
    formData.append('rentalPrice', houseDetails.rentalPrice);
    formData.append('rentalNumberOfRooms', houseDetails.rentalNumberOfRooms);
    formData.append('rentalStatus', houseDetails.rentalStatus);
    formData.append('businessLocationID', houseDetails.businessLocationID);
    formData.append('companyID', companyID);
    formData.append('apiType', 'registerHouses');

    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data === 'Successful') {
        setSubmitStatus({ 
          type: 'success', 
          message: 'House registered successfully!' 
        });
        setHouseDetails({
          houseNumber: '',
          rentalsPaymentInterval: '',
          rentalPrice: '',
          rentalNumberOfRooms: '',
          rentalStatus: '',
          businessLocationID: '',
        });
        setActiveSection('confirmation');
      } else {
        setSubmitStatus({ 
          type: 'error', 
          message: response.data || 'Registration failed. Please try again.' 
        });
      }
    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Network error submitting form. Please check your internet connection.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExcelUpload = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmitStatus({ type: '', message: '' });

    if (!excelFile) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Please select an Excel file.' 
      });
      setIsLoading(false);
      return;
    }

    if (!houseDetails.businessLocationID) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Please select a business location.' 
      });
      setIsLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append('excelHouses', excelFile);
    formData.append('businessLocationID', houseDetails.businessLocationID); // Fixed this line
    formData.append('companyID', companyID);
    formData.append('apiType', 'registerHousesExcel');

    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setSubmitStatus({ 
        type: response.data.includes('Successful') || response.data.includes('successful') ? 'success' : 'error',
        message: response.data 
      });
      
      if (response.data.includes('Successful') || response.data.includes('successful')) {
        setExcelFile(null);
        setActiveSection('confirmation');
      }
    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: error.response?.data || 'Error uploading file. Please try again.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isSingleFormValid = () => {
    return houseDetails.businessLocationID && 
           houseDetails.houseNumber.trim() && 
           houseDetails.rentalsPaymentInterval.trim() && 
           houseDetails.rentalPrice && 
           houseDetails.rentalNumberOfRooms && 
           houseDetails.rentalStatus;
  };

  const downloadTemplate = () => {
    // Create a simple Excel template structure
    const templateData = [
      ['houseNumber', 'rentalsPaymentInterval', 'rentalPrice', 'rentalNumberOfRooms', 'rentalStatus'],
      ['H-101', 'Monthly', '5000', '3', 'Available'],
      ['H-102', 'Monthly', '4500', '2', 'Occupied'],
      ['H-103', 'Quarterly', '13000', '4', 'Available']
    ];
    
    const csvContent = templateData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'houses_template.csv');
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Header />
      
      <main className="min-h-screen bg-gradient-to-b from-gray-50 to-white lg:ml-64 pt-16">
        <div className="p-6">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-6">
              <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
                <Home className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Register Houses</h1>
                <p className="text-gray-600 mt-1 lg:mt-2">Add new houses to your rental properties</p>
              </div>
            </div>

            {/* Progress Steps */}
            <div className="bg-white rounded-2xl p-4 lg:p-6 shadow-sm border border-gray-200 mb-6 lg:mb-8">
              <div className="flex items-center justify-between space-x-2 lg:space-x-0">
                {['Upload Method', 'House Details', 'Confirmation'].map((step, index) => (
                  <div key={index} className="flex items-center flex-1">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                      (activeSection === 'method' && index === 0) || 
                      (activeSection === 'details' && index <= 1) || 
                      (activeSection === 'confirmation' && index <= 2)
                        ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white' 
                        : 'bg-gray-100 text-gray-400'
                    }`}>
                      {index + 1}
                    </div>
                    <span className={`ml-2 text-sm lg:text-base ${
                      (activeSection === 'method' && index === 0) || 
                      (activeSection === 'details' && index <= 1) || 
                      (activeSection === 'confirmation' && index <= 2)
                        ? 'font-medium text-gray-900' 
                        : 'text-gray-500'
                    } hidden sm:inline`}>
                      {step}
                    </span>
                    {index < 2 && <div className="flex-1 h-0.5 bg-gray-200 mx-2 lg:mx-4"></div>}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
              {/* Main Form */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                  {/* Form Header */}
                  <div className="px-4 lg:px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-center space-x-3">
                      <PlusCircle className="w-6 h-6 text-blue-500" />
                      <h2 className="text-lg lg:text-xl font-bold text-gray-900">New House Registration</h2>
                    </div>
                    <p className="text-gray-600 mt-1 lg:mt-2 text-sm lg:text-base">Choose your preferred method to register houses</p>
                  </div>

                  {/* Form Content */}
                  <div className="p-4 lg:p-8">
                    {submitStatus.message && (
                      <div className={`mb-6 p-4 rounded-xl ${
                        submitStatus.type === 'success' 
                          ? 'bg-green-50 border border-green-200' 
                          : 'bg-red-50 border border-red-200'
                      }`}>
                        <div className="flex items-center">
                          {submitStatus.type === 'success' ? (
                            <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                          ) : (
                            <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
                          )}
                          <p className={`text-sm lg:text-base ${submitStatus.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                            {submitStatus.message}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Upload Method Selection - Only shown if no method selected */}
                    {!uploadMethod && (
                      <div className="mb-8">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Upload Method</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <button
                            onClick={() => {
                              setUploadMethod('Single Form');
                              setActiveSection('details');
                            }}
                            className="p-6 rounded-xl border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all duration-300 group"
                          >
                            <div className="flex flex-col items-center text-center">
                              <PlusCircle className="w-8 h-8 mb-3 text-gray-400 group-hover:text-blue-500" />
                              <h4 className="font-medium text-gray-900">Single Form</h4>
                              <p className="text-sm text-gray-600 mt-1">Register one house at a time</p>
                            </div>
                          </button>
                          <button
                            onClick={() => {
                              setUploadMethod('Upload Excel File');
                              setActiveSection('details');
                            }}
                            className="p-6 rounded-xl border-2 border-gray-200 hover:border-green-300 hover:bg-green-50 transition-all duration-300 group"
                          >
                            <div className="flex flex-col items-center text-center">
                              <FileSpreadsheet className="w-8 h-8 mb-3 text-gray-400 group-hover:text-green-500" />
                              <h4 className="font-medium text-gray-900">Excel Upload</h4>
                              <p className="text-sm text-gray-600 mt-1">Bulk upload with Excel file</p>
                            </div>
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Single Form */}
                    {uploadMethod === 'Single Form' && (
                      <form onSubmit={handleSingleFormSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
                          {/* Business Location */}
                          <div className="md:col-span-2">
                            <label htmlFor="businessLocationID" className="block text-sm font-medium text-gray-700 mb-2">
                              <div className="flex items-center">
                                <Building className="w-4 h-4 mr-2 text-gray-400" />
                                Business Location *
                              </div>
                            </label>
                            <select
                              id="businessLocationID"
                              name="businessLocationID"
                              value={houseDetails.businessLocationID}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                              required
                            >
                              <option value="">Select location...</option>
                              {businessLocations.map((location) => (
                                <option key={location.businessLocationID} value={location.businessLocationID}>
                                  {location.businessLocationName}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* House Number */}
                          <div>
                            <label htmlFor="houseNumber" className="block text-sm font-medium text-gray-700 mb-2">
                              <div className="flex items-center">
                                <Home className="w-4 h-4 mr-2 text-gray-400" />
                                House Number *
                              </div>
                            </label>
                            <input
                              type="text"
                              id="houseNumber"
                              name="houseNumber"
                              value={houseDetails.houseNumber}
                              onChange={handleInputChange}
                              placeholder="e.g., H-101, Block A-1"
                              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                              required
                            />
                          </div>

                          {/* Payment Interval */}
                          <div>
                            <label htmlFor="rentalsPaymentInterval" className="block text-sm font-medium text-gray-700 mb-2">
                              <div className="flex items-center">
                                <Clock className="w-4 h-4 mr-2 text-gray-400" />
                                Payment Interval *
                              </div>
                            </label>
                            <select
                              id="rentalsPaymentInterval"
                              name="rentalsPaymentInterval"
                              value={houseDetails.rentalsPaymentInterval}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                              required
                            >
                              <option value="">Select interval...</option>
                              <option value="Monthly">Monthly</option>
                              <option value="Quarterly">Quarterly</option>
                              <option value="Annually">Annually</option>
                            </select>
                          </div>

                          {/* Rental Price */}
                          <div>
                            <label htmlFor="rentalPrice" className="block text-sm font-medium text-gray-700 mb-2">
                              <div className="flex items-center">
                                <DollarSign className="w-4 h-4 mr-2 text-gray-400" />
                                Rental Price (ZMW) *
                              </div>
                            </label>
                            <input
                              type="number"
                              id="rentalPrice"
                              name="rentalPrice"
                              value={houseDetails.rentalPrice}
                              onChange={handleInputChange}
                              placeholder="0.00"
                              min="0"
                              step="0.01"
                              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                              required
                            />
                          </div>

                          {/* Number of Rooms */}
                          <div>
                            <label htmlFor="rentalNumberOfRooms" className="block text-sm font-medium text-gray-700 mb-2">
                              <div className="flex items-center">
                                <Bed className="w-4 h-4 mr-2 text-gray-400" />
                                Number of Rooms *
                              </div>
                            </label>
                            <input
                              type="number"
                              id="rentalNumberOfRooms"
                              name="rentalNumberOfRooms"
                              value={houseDetails.rentalNumberOfRooms}
                              onChange={handleInputChange}
                              placeholder="e.g., 3, 4"
                              min="1"
                              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                              required
                            />
                          </div>

                          {/* Status */}
                          <div>
                            <label htmlFor="rentalStatus" className="block text-sm font-medium text-gray-700 mb-2">
                              <div className="flex items-center">
                                <CheckCircle className="w-4 h-4 mr-2 text-gray-400" />
                                Status *
                              </div>
                            </label>
                            <select
                              id="rentalStatus"
                              name="rentalStatus"
                              value={houseDetails.rentalStatus}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                              required
                            >
                              <option value="">Select status...</option>
                              <option value="Available">Available</option>
                              <option value="Occupied">Occupied</option>
                              <option value="Under Maintenance">Under Maintenance</option>
                            </select>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-3 pt-6">
                          <button
                            type="button"
                            onClick={() => {
                              setUploadMethod('');
                              setActiveSection('method');
                              setSubmitStatus({ type: '', message: '' });
                            }}
                            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                          >
                            Back
                          </button>
                          <button
                            type="submit"
                            disabled={!isSingleFormValid() || isLoading}
                            className={`flex-1 py-3.5 px-6 rounded-xl font-medium transition-all duration-300 ${
                              isSingleFormValid() && !isLoading
                                ? 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white shadow-lg hover:shadow-xl'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            {isLoading ? (
                              <div className="flex items-center justify-center">
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                                Registering House...
                              </div>
                            ) : (
                              <div className="flex items-center justify-center">
                                <PlusCircle className="w-5 h-5 mr-2" />
                                Register House
                              </div>
                            )}
                          </button>
                        </div>
                      </form>
                    )}

                    {/* Excel Upload Form */}
                    {uploadMethod === 'Upload Excel File' && (
                      <form onSubmit={handleExcelUpload} className="space-y-6">
                        {/* Business Location */}
                        <div>
                          <label htmlFor="businessLocationIDExcel" className="block text-sm font-medium text-gray-700 mb-2">
                            <div className="flex items-center">
                              <Building className="w-4 h-4 mr-2 text-gray-400" />
                              Business Location *
                            </div>
                          </label>
                          <select
                            id="businessLocationIDExcel"
                            name="businessLocationID"  // Fixed: Changed from business_locationIDHousesExcel to businessLocationID
                            value={houseDetails.businessLocationID}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                            required
                          >
                            <option value="">Select location...</option>
                            {businessLocations.map((location) => (
                              <option key={location.businessLocationID} value={location.businessLocationID}>
                                {location.businessLocationName}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* File Upload */}
                        <div>
                          <label htmlFor="excelHouses" className="block text-sm font-medium text-gray-700 mb-2">
                            <div className="flex items-center">
                              <FileSpreadsheet className="w-4 h-4 mr-2 text-gray-400" />
                              Excel File *
                            </div>
                          </label>
                          
                          <div className="relative">
                            <input
                              type="file"
                              id="excelHouses"
                              name="excelHouses"
                              onChange={handleExcelFileChange}
                              accept=".xlsx,.xls,.csv"
                              className="hidden"
                              required
                            />
                            <label
                              htmlFor="excelHouses"
                              className="flex flex-col items-center justify-center w-full min-h-[128px] border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all duration-300"
                            >
                              <div className="flex flex-col items-center justify-center p-5">
                                {excelFile ? (
                                  <>
                                    <FileSpreadsheet className="w-8 h-8 text-green-500 mb-2" />
                                    <p className="text-sm font-medium text-gray-900 truncate max-w-xs">{excelFile.name}</p>
                                    <p className="text-xs text-gray-500 mt-1">{(excelFile.size / 1024).toFixed(2)} KB</p>
                                  </>
                                ) : (
                                  <>
                                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                                    <p className="text-sm text-gray-500 text-center">
                                      <span className="font-medium text-blue-500">Click to upload</span> or drag and drop
                                    </p>
                                    <p className="text-xs text-gray-400 mt-1">
                                      Excel files only (.xlsx, .xls, .csv)
                                    </p>
                                  </>
                                )}
                              </div>
                            </label>
                            {excelFile && (
                              <button
                                type="button"
                                onClick={() => setExcelFile(null)}
                                className="absolute top-2 right-2 p-1 bg-red-100 text-red-600 rounded-full hover:bg-red-200 transition-colors"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          
                          <p className="text-xs text-gray-500 mt-2">
                            Maximum file size: 10MB. Supported formats: .xlsx, .xls, .csv
                          </p>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-3 pt-6">
                          <button
                            type="button"
                            onClick={() => {
                              setUploadMethod('');
                              setActiveSection('method');
                              setExcelFile(null);
                              setSubmitStatus({ type: '', message: '' });
                            }}
                            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                          >
                            Back
                          </button>
                          <button
                            type="submit"
                            disabled={!houseDetails.businessLocationID || !excelFile || isLoading}
                            className={`flex-1 py-3.5 px-6 rounded-xl font-medium transition-all duration-300 ${
                              houseDetails.businessLocationID && excelFile && !isLoading
                                ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg hover:shadow-xl'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            {isLoading ? (
                              <div className="flex items-center justify-center">
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                                Uploading File...
                              </div>
                            ) : (
                              <div className="flex items-center justify-center">
                                <Upload className="w-5 h-5 mr-2" />
                                Upload Excel File
                              </div>
                            )}
                          </button>
                        </div>
                      </form>
                    )}

                    {/* Confirmation Screen */}
                    {activeSection === 'confirmation' && submitStatus.type === 'success' && (
                      <div className="text-center py-12">
                        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                          <CheckCircle className="w-10 h-10 text-green-500" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">Success!</h3>
                        <p className="text-gray-600 mb-8 max-w-md mx-auto">{submitStatus.message}</p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                          <button
                            onClick={() => {
                              setUploadMethod('');
                              setActiveSection('method');
                              setSubmitStatus({ type: '', message: '' });
                            }}
                            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-medium hover:from-blue-600 hover:to-purple-600 transition-all"
                          >
                            Register Another House
                          </button>
                          <button
                            onClick={() => window.location.href = '/ViewHouses'}
                            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                          >
                            View All Houses
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Form Footer */}
                  <div className="px-4 lg:px-8 py-4 lg:py-6 border-t border-gray-200 bg-gray-50">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Info className="w-4 h-4 text-blue-500" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Required Fields</p>
                          <p className="text-xs text-gray-500">All fields marked with * are required</p>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        {uploadMethod === 'Single Form' ? 'Single entry form' : 'Bulk upload via Excel'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Guidelines & Info */}
              <div className="space-y-6 lg:space-y-8">
                {/* Template Guidelines */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                    <FileSpreadsheet className="w-5 h-5 mr-2 text-blue-500" />
                    Excel Template Guidelines
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-700">Use the provided Excel template format</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-700">Include all required columns</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-700">Avoid special characters in house numbers</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-700">Keep numeric values in proper format</span>
                    </li>
                  </ul>
                </div>

                {/* Quick Stats */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <h3 className="font-bold text-gray-900 flex items-center">
                      <Home className="w-5 h-5 mr-2 text-purple-500" />
                      House Registration Stats
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Building className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-600">Available Locations</span>
                        </div>
                        <span className="font-medium text-gray-900">{businessLocations.length}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-600">Upload Methods</span>
                        </div>
                        <span className="font-medium text-gray-900">2</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <CheckCircle className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-600">Required Fields</span>
                        </div>
                        <span className="font-medium text-gray-900">6</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Download Template */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <h3 className="font-bold text-gray-900 flex items-center">
                      <Download className="w-5 h-5 mr-2 text-green-500" />
                      Download Template
                    </h3>
                  </div>
                  <div className="p-6">
                    <button
                      onClick={downloadTemplate}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all duration-300"
                    >
                      <Download className="w-5 h-5" />
                      <span>Download Excel Template</span>
                    </button>
                    <p className="text-xs text-gray-500 mt-3 text-center">
                      Use this template for bulk uploads
                    </p>
                  </div>
                </div>

                {/* Help Card */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <h3 className="font-bold text-gray-900 flex items-center">
                      <Info className="w-5 h-5 mr-2 text-orange-500" />
                      Need Help?
                    </h3>
                  </div>
                  <div className="p-6">
                    <p className="text-sm text-gray-600 mb-4">
                      Having trouble with the upload? Check our documentation or contact support.
                    </p>
                    <button className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm">
                      View Documentation
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default ModalUploadHousesForm;